import { Fade } from '@mui/material'
import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { DEFAULT_NOTIFICATION_SHOW_TIME } from '../../config'
import type { INotification, ITimer } from '.'
import { MessageContext } from '.'
import Notification from './Notification'
import * as S from './styles'

const MessageManagerProvider: React.FC<any> = ({ children }) => {
  const timer = useRef(0)
  const timersArr = useRef<ITimer[]>([])
  const [notifications, setNotifications] = useState<INotification[]>([])
  const [leavingIds, setLeavingIds] = useState<Set<number>>(new Set())

  useEffect(() => {
    const timersArrRef = timersArr.current

    return () => {
      timersArrRef.forEach((timer) => clearTimeout(timer.timeoutId))
    }
  }, [])

  const remove = useCallback((notificationId: number) => {
    setLeavingIds((prev) => new Set(prev).add(notificationId))
  }, [])

  const handleExited = useCallback((notificationId: number) => {
    setNotifications((current) => current.filter((n) => n.id !== notificationId))
    setLeavingIds((prev) => {
      const next = new Set(prev)
      next.delete(notificationId)
      return next
    })
  }, [])

  const timerCallback = useCallback(
    (notification: INotification) => {
      remove(notification.id)
      timersArr.current = timersArr.current.filter((timer) => timer.id !== notification.id)
    },
    [remove],
  )

  const clearErrorNotifications = useCallback(() => {
    setNotifications((currentNotifications) => {
      const errorIds = currentNotifications
        .filter((n) => n.message.status === 'error')
        .map((n) => n.id)

      if (errorIds.length > 0) {
        setLeavingIds((prev) => {
          const next = new Set(prev)
          errorIds.forEach((id) => next.add(id))
          return next
        })
      }

      return currentNotifications
    })
  }, [])

  const show = useCallback(
    (message = {}, timeout = DEFAULT_NOTIFICATION_SHOW_TIME) => {
      const id = timer.current
      timer.current += 1
      const notification = {
        close: () => remove(id),
        id,
        message,
        timeout,
      }
      if (timeout !== null) {
        const timeoutId = window.setTimeout(() => {
          timerCallback(notification)
        }, timeout)

        timersArr.current.push({
          id: notification.id,
          notification,
          remaining: timeout,
          start: new Date().getTime(),
          timeoutId,
        })
      }

      setNotifications((state) => [notification, ...state])

      return notification
    },
    [remove, timerCallback],
  )

  const getCurrentTimer = (notification: INotification) => {
    const currentTimerIndex = timersArr.current.findIndex((timer) => timer.id === notification.id)
    return timersArr.current[currentTimerIndex]
  }

  const pauseTimer = (notification: INotification) => {
    const currentTimer = getCurrentTimer(notification)
    if (currentTimer) {
      currentTimer.remaining = currentTimer.remaining - (new Date().getTime() - currentTimer.start)
      window.clearTimeout(currentTimer.timeoutId)
    }
  }

  const resumeTimer = (notification: INotification) => {
    const currentTimer = getCurrentTimer(notification)
    if (currentTimer) {
      currentTimer.start = new Date().getTime()
      currentTimer.timeoutId = window.setTimeout(
        () => timerCallback(notification),
        currentTimer.remaining,
      )
    }
  }

  return (
    <>
      <MessageContext.Provider value={{ clearErrorNotifications, remove, show }}>
        {children}
      </MessageContext.Provider>
      {notifications.length > 0 && (
        <S.Container>
          {notifications.map((notification) => (
            <Fade
              key={notification.id}
              in={!leavingIds.has(notification.id)}
              timeout={250}
              onExited={() => handleExited(notification.id)}
            >
              <S.NotificationWrapper
                onMouseEnter={notification.timeout ? () => pauseTimer(notification) : undefined}
                onMouseLeave={notification.timeout ? () => resumeTimer(notification) : undefined}
              >
                <Notification
                  onClose={notification.close}
                  title={notification.message.title}
                  type={notification.message.status ?? 'info'}
                  content={notification.message.text}
                  apiMessage={notification.message.apiMessage}
                  action={
                    notification.message.actionBtn
                      ? {
                          label: notification.message.actionBtn.label,
                          onClick: notification.message.actionBtn.action,
                        }
                      : undefined
                  }
                />
              </S.NotificationWrapper>
            </Fade>
          ))}
        </S.Container>
      )}
    </>
  )
}

export default MessageManagerProvider
